import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { TrendingUp, TrendingDown, Minus, Lightbulb, ArrowRight, Download, FileText, MoreVertical } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { getModuleIcon, type ModuleData } from '../../../content';
import { Skeleton } from '../../../components/shared/Skeleton';
import { type LearnerAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
import { parseGroupNum } from '../shared/groupUtils';
import { downloadCsv } from '../../../utils/csvExport';
import { useGrowthData } from '../hooks/useGrowthData';
import { useModuleMastery } from '../hooks/useModuleMastery';
import { useLatestAssessment } from '../hooks/useLatestAssessment';
import { DataFreshness } from '../../shared/DataFreshness';
import { getChildLastActivity } from '../shared/childActivity';
import { ParentV2Route } from '../ParentSidebarV2';
import { SubjectModuleRow } from '../../shared/SubjectModuleRow';
import { SubjectsSummaryHero } from '../../shared/SubjectsSummaryHero';
import { computeSkillMasteries } from '../../../mastery';
import { getMasteryBand } from '../../shared/masteryBands';
interface VoortgangPageProps {
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string) => void;
  onNavigate?: (route: ParentV2Route) => void;
}
type SortOption = 'attention' | 'strong' | 'alpha';
function ParentModuleExpandContent({
  module,
  childData,
  selectedGroup,
  mastery,
  selectedChild
}: {
  module: ModuleData;
  childData: AppState | null;
  selectedGroup: string;
  mastery: number;
  selectedChild: LearnerAccount;
}) {
  const groupNum = parseGroupNum(selectedGroup);
  const skillMasteries = useMemo(() => {
    if (!childData) return [];
    return computeSkillMasteries(module.id, groupNum, childData.questionStates || {}, childData.familyMastery);
  }, [module.id, groupNum, childData]);
  const suggestions = useMemo(() => {
    const name = selectedChild.displayName;
    if (mastery < 35) {
      return ['Korte sessies van 5-10 min, focus op één sub-vaardigheid tegelijk.', 'Vier elke kleine winst — motivatie is hier belangrijker dan tempo.'];
    }
    if (mastery < 75) {
      return ['Houd het ritme van 3-4 dagen per week vast.', 'Bekijk samen 1 foutje per sessie — vragen werken beter dan antwoorden geven.'];
    }
    return [`Goed bezig! Hier mag ${name} af en toe een uitdaging op een hoger niveau proberen.`, 'Doe een toets om het niveau te bevestigen.'];
  }, [mastery, selectedChild.displayName]);
  const humanizeSkill = (skill: string) => {
    const cleaned = skill.replace(/_/g, ' ').toLowerCase();
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  };
  return <div className="p-4 sm:pl-18 space-y-6" data-id="element-8517" data-mp="1rfcqjkw4zev2">
      {/* Sub-skills */}
      <section data-id="element-8518" data-mp="263h98ov6vqdu">
        <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8519" data-mp="2640i7g2v34fq">
          Wat oefent {selectedChild.displayName} hier?
        </h3>
        {skillMasteries.length > 0 ? <div className="space-y-2" data-id="element-8520" data-mp="1dea5nbgvoalq">
            {skillMasteries.map(skill => {
          const trendIcon = skill.trend === 'improving' ? <TrendingUp className="w-3 h-3" data-id="element-8521" data-mp="1nczmr20y9r0x" /> : skill.trend === 'struggling' ? <TrendingDown className="w-3 h-3" data-id="element-8522" data-mp="1idwgxfd5bgrw" /> : <Minus className="w-3 h-3" data-id="element-8523" data-mp="1ifzfwowa0k8r" />;
          const trendColor = skill.trend === 'improving' ? 'text-emerald-600 bg-emerald-50' : skill.trend === 'struggling' ? 'text-rose-600 bg-rose-50' : 'text-slate-500 bg-slate-100';
          return <div key={skill.skill} className="p-3 border border-slate-200 bg-white rounded-xl shadow-sm" data-id="element-8524" data-mp="18bu48q17ryd2">
                  <div className="flex items-center gap-3 mb-2" data-id="element-8525" data-mp="1hbh95lt5jmav">
                    <div className="flex-1 min-w-0" data-id="element-8526" data-mp="1h9mjxvaul80z">
                      <div className="text-sm font-bold text-slate-900 truncate" data-id="element-8527" data-mp="1mbloh8ibqazi">
                        {humanizeSkill(skill.skill)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0" data-id="element-8528" data-mp="1w03nbk71m80i">
                      <span className="text-sm font-black text-slate-800" data-id="element-8529" data-mp="20279b2rc8v01">
                        {skill.masteryScore}%
                      </span>
                      <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${trendColor}`} data-id="element-8530" data-mp="252mkbno5dqeu">
                        {trendIcon}
                      </div>
                    </div>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-8531" data-mp="2f4gtskqh2y20">
                    <div className={`h-full rounded-full transition-all ${getMasteryBand(skill.masteryScore).barClass}`} style={{
                width: `${skill.masteryScore}%`
              }} data-id="element-8532" data-mp="2a5dqb4q54zw5" />
                  </div>
                </div>;
        })}
          </div> : <div className="text-sm text-slate-500 italic" data-id="element-8533" data-mp="2t4rndvj62qqv">
            Nog geen sub-vaardigheden gemeten.
          </div>}
      </section>

      {/* Suggestions */}
      <section data-id="element-8534" data-mp="2p1lj2tfm79n1">
        <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-8535" data-mp="3llarpsf61ca9">
          <Lightbulb className="w-3 h-3" data-id="element-8536" data-mp="3gu2e0k6sz4ra" />
          Wat helpt thuis?
        </h3>
        <div className="p-3 border border-slate-200 bg-white rounded-xl shadow-sm" data-id="element-8537" data-mp="1ex22eciarogt">
          <ul className="space-y-2" data-id="element-8538" data-mp="1ezefab9778n0">
            {suggestions.map((suggestion, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-id="element-8539" data-mp="1jy7qty4xcpkj" data-mpi={i}>
                <span className="text-emerald-500 font-bold flex-shrink-0 mt-0.5" data-id="element-8540" data-mp="1a5tp35dujiyd">
                  •
                </span>
                <span data-id="element-8541" data-mp="w5iv3th8po8z">{suggestion}</span>
              </li>)}
          </ul>
        </div>
      </section>
    </div>;
}
export function VoortgangPage({
  linkedLearners,
  selectedChild,
  childData,
  selectedGroup,
  getLearnerModuleMastery,
  setViewingLearner,
  onNavigate
}: VoortgangPageProps) {
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('attention');
  const [showMenu, setShowMenu] = useState(false);
  const groupNum = parseGroupNum(selectedGroup);
  const {
    report: latestAssessmentReport,
    isLoading: isAssessmentLoading
  } = useLatestAssessment(selectedChild, selectedGroup);
  const growthData = useGrowthData(selectedChild, childData, selectedGroup, getLearnerModuleMastery);
  const moduleMasteryList = useModuleMastery(selectedChild, getLearnerModuleMastery);
  const handleExportCsv = () => {
    if (!selectedChild || !growthData) return;
    const headers = ['Vak', 'Beheersing (%)', 'Niveau', 'Trend', 'Trend (%)'];
    const rows = growthData.modules.map(m => {
      const trendText = m.trend === 'up' ? 'Stijgt' : m.trend === 'down' ? 'Daalt' : 'Stabiel';
      return [`"${m.module.title}"`, m.mastery, `"${getMasteryBand(m.mastery).label}"`, `"${trendText}"`, m.delta].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const safeName = selectedChild.displayName.replace(/[^a-zA-Z0-9]/g, '_');
    downloadCsv(csvContent, `voortgang_${safeName}`);
    setShowMenu(false);
  };
  const sortedModules = useMemo(() => {
    const list = [...moduleMasteryList];
    return list.sort((a, b) => {
      if (sortBy === 'attention') {
        return a.mastery - b.mastery;
      }
      if (sortBy === 'strong') {
        return b.mastery - a.mastery;
      }
      // alpha
      return a.module.title.localeCompare(b.module.title);
    });
  }, [moduleMasteryList, sortBy]);
  if (!selectedChild || !growthData) {
    return <PageLayout title="Vakken" description="Bekijk de voortgang per vak" data-id="element-8542" data-mp="1zm5vm1u19qx9">
        <div className="text-center py-20 text-slate-500 font-medium" data-id="element-8543" data-mp="1zo8iiczw33xr">
          Geen data beschikbaar.
        </div>
      </PageLayout>;
  }
  const topSubject = growthData.strengths[0]?.module.title;
  const growthSubject = growthData.areasForGrowth[0]?.module.title;
  return <PageLayout title="Vakken" description={`Bekijk de voortgang per vak van ${selectedChild.displayName}`} actions={<>
          <DataFreshness updatedAt={getChildLastActivity(childData)} data-id="element-8545" data-mp="19bvk5bp41gkf" />
          <div className="relative" data-id="element-8546" data-mp="19dyaxxy37dq7">
            <button onClick={() => setShowMenu(!showMenu)} className="flex items-center justify-center w-9 h-9 rounded-xl text-slate-500 hover:bg-slate-100 transition-all border border-transparent hover:border-slate-200" data-id="element-8547" data-mp="14csg9ukv9ge1">
              <MoreVertical className="w-5 h-5" data-id="element-8548" data-mp="uleknxw1d0q6" />
            </button>
            {showMenu && <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} data-id="element-8549" data-mp="lmstxfgeufeb" />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-200 z-50 overflow-hidden" data-id="element-8550" data-mp="go98rh9n57n0">
                  <button onClick={handleExportCsv} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors text-left" data-id="element-8551" data-mp="bqpwgmwi74xp">
                    <Download className="w-4 h-4" data-id="element-8552" data-mp="21769thl4cxm9" />
                    Exporteer CSV
                  </button>
                </div>
              </>}
          </div>
        </>} data-id="element-8544" data-mp="3fzvwkzpuvz2o">
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-8553" data-mp="2a7w0y7zfpztn">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-8554" data-mp="267c4l6mor08k" />

        <div className="space-y-8" data-id="element-8555" data-mp="1ii9bn2mlpkyr">
          {/* Summary Hero */}
          <SubjectsSummaryHero variant="parent" childName={selectedChild.displayName} overallMastery={growthData.overallMastery} groupNum={groupNum} topSubject={topSubject} growthSubject={growthSubject} data-id="element-8556" data-mp="1djg03fqvk418" />

          {/* Latest Assessment Result (Compact Chip) */}
          <section data-id="element-8557" data-mp="yrnw1zwj6cet">
            {isAssessmentLoading ? <Card className="p-4 border-slate-200 shadow-sm bg-white flex items-center justify-between" data-id="element-8558" data-mp="ur402znp1eu9">
                <div className="flex items-center gap-3" data-id="element-8559" data-mp="kngj6l2xliy1">
                  <Skeleton className="w-8 h-8 rounded-lg" data-id="element-8560" data-mp="peoszi86b6bq" />
                  <Skeleton className="h-4 w-32" data-id="element-8561" data-mp="pg9opb684vb6" />
                </div>
                <Skeleton className="h-8 w-24 rounded-lg" data-id="element-8562" data-mp="ar3pvobat73t" />
              </Card> : latestAssessmentReport ? <Card role="button" tabIndex={0} onClick={() => onNavigate?.('toetsen')} onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onNavigate?.('toetsen');
            }
          }} className="p-4 border-slate-200 shadow-sm bg-white flex items-center justify-between hover:bg-slate-50 hover:border-emerald-200 transition-all cursor-pointer group focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none" data-id="element-8563" data-mp="fpf0i19f3ln3">
                <div className="flex items-center gap-3" data-id="element-8564" data-mp="1pn5b139ulazr">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-indigo-50 text-indigo-600" data-id="element-8565" data-mp="1pmlthno40om9">
                    <FileText className="w-4 h-4" data-id="element-8566" data-mp="1kqellpc5l31o" />
                  </div>
                  <div data-id="element-8567" data-mp="1fvheinyfp3rf">
                    <div className="text-sm font-bold text-slate-900" data-id="element-8568" data-mp="1fuza341mbj8f">
                      Laatste toets:{' '}
                      {latestAssessmentReport.overallEstimatedLevelCode}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-8569" data-mp="11xssvdwjlslb">
                      {new Date(latestAssessmentReport.completedAt).toLocaleDateString('nl-NL', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm font-bold text-emerald-600 group-hover:text-emerald-700" data-id="element-8570" data-mp="2md4jkxq009ic">
                  Naar Toetsen <ArrowRight className="w-4 h-4" data-id="element-8571" data-mp="2twn9lnxnt30f" />
                </div>
              </Card> : <Card role="button" tabIndex={0} onClick={() => onNavigate?.('toetsen')} onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onNavigate?.('toetsen');
            }
          }} className="p-4 border border-dashed border-slate-200 bg-slate-50/40 flex items-center justify-between hover:bg-slate-50 hover:border-emerald-200 transition-all cursor-pointer group focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none" data-id="element-8572" data-mp="2ytx59lbn6awp">
                <div className="flex items-center gap-3" data-id="element-8573" data-mp="6sx3shkdx6r">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-400" data-id="element-8574" data-mp="8ce5lb1oc9l">
                    <FileText className="w-4 h-4" data-id="element-8575" data-mp="2pwcw008pzb3" />
                  </div>
                  <div className="text-sm font-bold text-slate-500" data-id="element-8576" data-mp="3uu8cceozhrye">
                    Nog geen toets gemaakt
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm font-bold text-slate-400 group-hover:text-emerald-600" data-id="element-8577" data-mp="cq6qvmu3narb">
                  Naar Toetsen <ArrowRight className="w-4 h-4" data-id="element-8578" data-mp="7rvf36k8uq6g" />
                </div>
              </Card>}
          </section>

          {/* All Modules List */}
          <section data-id="element-8579" data-mp="hmx667y2y2bo">
            <div className="flex items-center justify-between mb-4" data-id="element-8580" data-mp="hngf4z5r5gdk">
              <h3 className="text-lg font-black text-slate-900" data-id="element-8581" data-mp="vds62uqlp934">
                Alle geoefende vakken
              </h3>
              <select value={sortBy} onChange={e => setSortBy(e.target.value as SortOption)} className="text-sm border-slate-200 rounded-lg bg-white text-slate-700 font-medium focus:ring-emerald-500 focus:border-emerald-500 py-1.5 pl-3 pr-8 shadow-sm" data-id="element-8582" data-mp="qgsaxevahqqs">
                <option value="attention" data-id="element-8583" data-mp="1jy5c0jrtmi7i">Aandacht eerst</option>
                <option value="strong" data-id="element-8584" data-mp="1o0a072zi2mmr">Sterk eerst</option>
                <option value="alpha" data-id="element-8585" data-mp="1a227j79hy7he">Alfabetisch</option>
              </select>
            </div>

            <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-8586" data-mp="1f0n32tdt02zi">
              {sortedModules.map(m => {
              const IconComp = getModuleIcon(m.module.icon);
              const growthEntry = growthData.modules.find(gm => gm.module.id === m.module.id);
              const trend = growthEntry?.trend || 'stable';
              const delta = growthEntry?.delta || 0;
              const isExpanded = selectedModuleId === m.module.id;
              return <SubjectModuleRow key={m.module.id} variant="parent" moduleId={m.module.id} moduleTitle={m.module.title} moduleIcon={<IconComp className="w-5 h-5" data-id="element-8588" data-mp="38xga0et41d5" />} mastery={m.mastery} trend={trend} trendDelta={delta} isExpanded={isExpanded} onToggle={() => setSelectedModuleId(isExpanded ? null : m.module.id)} data-id="element-8587" data-mp="heful8edpqhv">
                    <ParentModuleExpandContent module={m.module} childData={childData} selectedGroup={selectedGroup} mastery={m.mastery} selectedChild={selectedChild} data-id="element-8589" data-mp="m6qlj9cwkndp" />
                  </SubjectModuleRow>;
            })}
            </Card>
          </section>
        </div>
      </div>
    </PageLayout>;
}